import {
  Badge,
  Button,
  Card,
  CardContent,
  DatePicker,
  Input,
  NumberField,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StackedList,
  Switch,
} from "@beweegbaas/ui";
import { parseDate, type DateValue } from "@internationalized/date";
import { useDebouncedCallback } from "@tanstack/react-pacer";
import { Link, createFileRoute, getRouteApi, useRouter } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useState } from "react";
import { ConfirmButton } from "#/components/confirm-button";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { EditContactgegevensDialog } from "#/modules/admin/components/edit-contactgegevens-dialog";
import { SchoolDetailHeader } from "#/modules/admin/components/school-detail-header";
import {
  deleteSchoolFn,
  updateLicentieAantalLeerlingenFn,
  updateLicentieEndDateFn,
  updateLicentieTypeFn,
  updateMaxDocentenFn,
  updateExcludeFromStatisticsFn,
  updateIsZuurpruimFn,
  updateVakantieregioFn,
  updateSchoolAliasFn,
  updateSchoolBestuurFn,
  updateSchoolBrinFn,
  updateSchoolNaamFn,
  updateVerwerkersovereenkomstFn,
} from "#/modules/admin/schools.functions";
import { stripTimeFromIso } from "#/modules/util.helpers/utils.date";
import { getLicentieTypeTranslation } from "#/modules/util.i18n/enum-mapping";
import { m } from "#/paraglide/messages";
import type { components } from "#/schema";

type Vakantieregio = components["schemas"]["Vakantieregio"];
const VAKANTIEREGIOS: Vakantieregio[] = ["Noord", "Midden", "Zuid"];
const GEEN_REGIO = "geen" as const;

const layoutApi = getRouteApi("/$locale/{-$schoolyear}/_authenticated/admin/schools/$id");

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/admin/schools/$id/overview",
)({
  component: AdminSchoolDetail,
  errorComponent: OverviewError,
});

function OverviewError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}

function SchoolKpiTile({ title, value }: { title: string; value: string }) {
  return (
    <Card size="sm">
      <CardContent>
        <p className="text-muted-foreground text-sm">{title}</p>
        <p className="text-xl font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

function AdminSchoolDetail() {
  const { id, locale, schoolyear } = Route.useParams();
  const { school, docentenCount, kpis } = layoutApi.useLoaderData();
  const router = useRouter();

  const updateMaxDocenten = useServerFn(updateMaxDocentenFn);
  const updateSchoolAlias = useServerFn(updateSchoolAliasFn);
  const updateSchoolNaam = useServerFn(updateSchoolNaamFn);
  const updateSchoolBrin = useServerFn(updateSchoolBrinFn);
  const updateSchoolBestuur = useServerFn(updateSchoolBestuurFn);
  const updateLicentieType = useServerFn(updateLicentieTypeFn);
  const updateLicentieEndDate = useServerFn(updateLicentieEndDateFn);
  const updateLicentieAantalLeerlingen = useServerFn(updateLicentieAantalLeerlingenFn);
  const updateVerwerkersovereenkomst = useServerFn(updateVerwerkersovereenkomstFn);
  const updateIsZuurpruim = useServerFn(updateIsZuurpruimFn);
  const updateVakantieregio = useServerFn(updateVakantieregioFn);
  const updateExcludeFromStatistics = useServerFn(updateExcludeFromStatisticsFn);
  const deleteSchool = useServerFn(deleteSchoolFn);

  const [maxDocenten, setMaxDocenten] = useState(school?.maxDocenten ?? 0);
  const [alias, setAlias] = useState(school?.alias ?? "");
  const [name, setName] = useState(school?.name ?? "");
  const [brin, setBrin] = useState(school?.brin ?? "");
  const [bestuursnaam, setBestuursnaam] = useState(school?.bestuursnaam ?? "");
  const [bestuursnummer, setBestuursnummer] = useState(school?.bestuursnummer ?? "");
  const [licentieType, setLicentieType] = useState<components["schemas"]["LicentieType"]>(
    school?.licentie?.licentieType ?? "Proefperiode",
  );
  const [endDate, setEndDate] = useState<DateValue | null>(() => {
    if (!school?.licentie?.endDate) return null;
    return parseDate(stripTimeFromIso(school.licentie.endDate));
  });
  const [aantalLeerlingen, setAantalLeerlingen] = useState<number | null>(
    school?.licentie?.aantalLeerlingen ?? null,
  );
  const [isDeleting, setIsDeleting] = useState(false);

  // Renaming the school regenerates its alias server-side, so the loaded alias can change while this
  // field was never touched. Re-seed it, or the next blur writes the pre-rename alias straight back.
  const [loadedAlias, setLoadedAlias] = useState(school?.alias ?? "");
  if ((school?.alias ?? "") !== loadedAlias) {
    setLoadedAlias(school?.alias ?? "");
    setAlias(school?.alias ?? "");
  }

  async function refresh() {
    await router.invalidate();
  }

  // NumberField fires onValueChange per typed digit — debounce the save so "300" is one request.
  const saveMaxDocenten = useDebouncedCallback(
    async (newValue: number) => {
      try {
        await updateMaxDocenten({ data: { schoolId: id, maxDocenten: newValue } });
      } catch {
        // toastErrorMiddleware already surfaced the error
        return;
      }
      await refresh();
    },
    { wait: 500 },
  );

  const saveAantalLeerlingen = useDebouncedCallback(
    async (newValue: number) => {
      try {
        await updateLicentieAantalLeerlingen({
          data: { schoolId: id, aantalLeerlingen: newValue },
        });
      } catch {
        // toastErrorMiddleware already surfaced the error
        return;
      }
      await refresh();
    },
    { wait: 500 },
  );

  if (!school) {
    return null;
  }

  async function handleAliasBlur() {
    const trimmed = alias.trim();
    if (trimmed === (school?.alias ?? "")) return;
    try {
      await updateSchoolAlias({ data: { schoolId: id, alias: trimmed || null } });
    } catch {
      // toastErrorMiddleware already surfaced the error
      return;
    }
    await refresh();
  }

  // Renaming re-slugs the school: the "Bekijk school" link only points at the school's new URL after
  // the refresh below.
  async function handleNaamBlur() {
    const trimmed = name.trim();
    if (trimmed === (school?.name ?? "")) return;
    // A StackedList row has no slot for an inline error: reject by snapping back to the stored value.
    if (trimmed.length === 0) {
      setName(school?.name ?? "");
      return;
    }
    try {
      await updateSchoolNaam({ data: { schoolId: id, name: trimmed } });
    } catch {
      // toastErrorMiddleware already surfaced the error
      return;
    }
    await refresh();
  }

  async function handleBrinBlur() {
    const normalized = brin.trim().toUpperCase();
    if (normalized === (school?.brin ?? "")) return;
    if (normalized.length !== 6) {
      setBrin(school?.brin ?? "");
      return;
    }
    try {
      await updateSchoolBrin({ data: { schoolId: id, brin: normalized } });
    } catch {
      // toastErrorMiddleware already surfaced the error
      setBrin(school?.brin ?? "");
      return;
    }
    await refresh();
  }

  // Both bestuur fields save through one endpoint, so a blur on either sends the pair.
  async function handleBestuurBlur() {
    const naam = bestuursnaam.trim();
    const nummer = bestuursnummer.trim();
    if (naam === (school?.bestuursnaam ?? "") && nummer === (school?.bestuursnummer ?? "")) return;
    try {
      await updateSchoolBestuur({
        data: { schoolId: id, bestuursnaam: naam || null, bestuursnummer: nummer || null },
      });
    } catch {
      // toastErrorMiddleware already surfaced the error
      return;
    }
    await refresh();
  }

  function handleMaxDocentenChange(newValue: number | null) {
    if (newValue === null) return;
    setMaxDocenten(newValue);
    saveMaxDocenten(newValue);
  }

  async function handleLicentieTypeChange(newValue: "Proefperiode" | "Abonnement" | null) {
    if (newValue === null || !school?.licentie) return;
    setLicentieType(newValue);
    try {
      await updateLicentieType({ data: { schoolId: id, licentieType: newValue } });
    } catch {
      // toastErrorMiddleware already surfaced the error
      return;
    }
    await refresh();
  }

  async function handleEndDateChange(date: DateValue | null) {
    if (date === null) return;
    setEndDate(date);
    try {
      await updateLicentieEndDate({
        data: { schoolId: id, endDate: `${date.toString()}T00:00:00.000Z` },
      });
    } catch {
      // toastErrorMiddleware already surfaced the error
      return;
    }
    await refresh();
  }

  function handleAantalLeerlingenChange(newValue: number | null) {
    if (newValue === null) return;
    setAantalLeerlingen(newValue);
    saveAantalLeerlingen(newValue);
  }

  async function handleVerwerkersovereenkomstChange(newValue: boolean) {
    try {
      await updateVerwerkersovereenkomst({
        data: { schoolId: id, verwerkersovereenkomst: newValue },
      });
    } catch {
      // toastErrorMiddleware already surfaced the error
      return;
    }
    await refresh();
  }

  async function handleVakantieregioChange(vakantieregio: Vakantieregio | null) {
    try {
      await updateVakantieregio({ data: { schoolId: id, vakantieregio } });
    } catch {
      // toastErrorMiddleware already surfaced the error
      return;
    }
    await refresh();
  }

  async function handleIsZuurpruimChange(newValue: boolean) {
    try {
      await updateIsZuurpruim({
        data: { schoolId: id, isZuurpruim: newValue },
      });
    } catch {
      // toastErrorMiddleware already surfaced the error
      return;
    }
    await refresh();
  }

  async function handleExcludeFromStatisticsChange(newValue: boolean) {
    try {
      await updateExcludeFromStatistics({
        data: { schoolId: id, excludeFromStatistics: newValue },
      });
    } catch {
      // toastErrorMiddleware already surfaced the error
      return;
    }
    await refresh();
  }

  // Success throws a redirect to the schools overview; failure rejections propagate to
  // ConfirmButton, which swallows them (middleware already toasted).
  async function handleDeleteSchool() {
    setIsDeleting(true);
    try {
      await deleteSchool({ data: { schoolId: id, locale, schoolyear } });
    } finally {
      setIsDeleting(false);
    }
  }

  const headerActions = (
    <>
      {school.slug && (
        <Button
          variant="secondary"
          size="xl"
          render={
            <Link
              to="/$locale/{-$schoolyear}/$school/lessons/today"
              params={{ locale, schoolyear, school: school.slug }}
              // No hover-preload: school loaders derive their tenant from window.location, which
              // still points at this ADMIN page during a preload — the RPCs would go out without
              // a tenant header and 400 (GlitchTip PORTAL-V). Navigation itself is fine: the URL
              // has committed by the time the loaders run.
              preload={false}
            />
          }
          nativeButton={false}
        >
          Bekijk school
          <IconMaterialSymbolsArrowRightAlt className="h-4 w-4" />
        </Button>
      )}
      <ConfirmButton
        variant="destructive"
        destructive
        onClick={handleDeleteSchool}
        confirmTitle={m["admin.scholen.delete.confirm_title"]()}
        confirmMessage={m["admin.scholen.delete.confirm_message"]({ schoolName: school.name })}
        disabled={isDeleting}
        size="xl"
      >
        <IconLucideTrash2 className="h-4 w-4" />
        {m["admin.scholen.delete.button"]()}
      </ConfirmButton>
    </>
  );

  return (
    <Page>
      <SchoolDetailHeader
        schoolName={school.name}
        schoolId={id}
        docentenCount={docentenCount}
        actions={headerActions}
        maxDocenten={school.maxDocenten}
      />
      <Page.Container>
        <Page.Body>
          {kpis && (
            <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
              <SchoolKpiTile title="Docenten" value={String(kpis.docenten)} />
              <SchoolKpiTile
                title={m["nav.leerlingen"]()}
                value={kpis.leerlingen.toLocaleString("nl-NL")}
              />
              <SchoolKpiTile title={m["nav.klassen"]()} value={String(kpis.klassen)} />
              <SchoolKpiTile
                title={m["admin.dashboard.lessen_gegeven"]()}
                value={kpis.lessenGegeven.toLocaleString("nl-NL")}
              />
              <SchoolKpiTile
                title="Scores ingevoerd"
                value={kpis.scoresIngevoerd.toLocaleString("nl-NL")}
              />
              <SchoolKpiTile
                title={m["admin.school.laatste_les"]()}
                value={
                  kpis.laatsteLesAfgerondAt
                    ? new Date(kpis.laatsteLesAfgerondAt).toLocaleDateString("nl-NL")
                    : "Nooit"
                }
              />
            </div>
          )}
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <StackedList>
              <StackedList.Content>
                <StackedList.Item>
                  <StackedList.Term>Aangemaakt op</StackedList.Term>
                  <StackedList.DescriptionText>
                    {school.createdAt
                      ? new Date(school.createdAt).toLocaleDateString("nl-NL")
                      : "-"}
                  </StackedList.DescriptionText>
                </StackedList.Item>
                <StackedList.Item>
                  <StackedList.Term>{m["common.fields.name"]()}</StackedList.Term>
                  <StackedList.DescriptionText>
                    <Input
                      aria-label="Naam"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      onBlur={() => void handleNaamBlur()}
                      maxLength={150}
                      className="w-48"
                    />
                  </StackedList.DescriptionText>
                </StackedList.Item>
                <StackedList.Item>
                  <StackedList.Term>{m["brin.brin"]()}</StackedList.Term>
                  <StackedList.DescriptionText>
                    <div className="flex items-center gap-2">
                      <Input
                        aria-label={m["brin.brin"]()}
                        value={brin}
                        onChange={(e) => setBrin(e.target.value.toUpperCase())}
                        onBlur={() => void handleBrinBlur()}
                        maxLength={6}
                        className="w-48"
                      />
                      <Badge variant={school.isOfficialBrin ? "green" : "yellow"} size="sm">
                        {school.isOfficialBrin
                          ? m["school.info.brin_official"]()
                          : m["school.info.brin_custom"]()}
                      </Badge>
                    </div>
                  </StackedList.DescriptionText>
                </StackedList.Item>
                <StackedList.Item>
                  <StackedList.Term>{m["school.info.alias"]()}</StackedList.Term>
                  <StackedList.DescriptionText>
                    <Input
                      aria-label={m["school.info.alias"]()}
                      value={alias}
                      onChange={(e) => setAlias(e.target.value)}
                      onBlur={() => void handleAliasBlur()}
                      maxLength={150}
                      className="w-48"
                    />
                  </StackedList.DescriptionText>
                </StackedList.Item>
                <StackedList.Item>
                  <StackedList.Term>Contactgegevens</StackedList.Term>
                  <StackedList.DescriptionText>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div>
                          {school.address} {school.houseNumber}
                        </div>
                        <div>
                          {school.postalCode} {school.city}
                        </div>
                        {school.phoneNumber && <div>{school.phoneNumber}</div>}
                      </div>
                      <EditContactgegevensDialog school={school} />
                    </div>
                  </StackedList.DescriptionText>
                </StackedList.Item>
                <StackedList.Item>
                  <StackedList.Term>{m["school.info.foundation"]()}</StackedList.Term>
                  <StackedList.DescriptionText>
                    <Input
                      aria-label={m["school.info.foundation"]()}
                      value={bestuursnaam}
                      onChange={(e) => setBestuursnaam(e.target.value)}
                      onBlur={() => void handleBestuurBlur()}
                      maxLength={150}
                      className="w-48"
                    />
                  </StackedList.DescriptionText>
                </StackedList.Item>
                <StackedList.Item>
                  <StackedList.Term>Bestuursnummer</StackedList.Term>
                  <StackedList.DescriptionText>
                    <Input
                      aria-label="Bestuursnummer"
                      value={bestuursnummer}
                      onChange={(e) => setBestuursnummer(e.target.value)}
                      onBlur={() => void handleBestuurBlur()}
                      maxLength={150}
                      className="w-48"
                    />
                  </StackedList.DescriptionText>
                </StackedList.Item>
                <StackedList.Item>
                  <StackedList.Term>Vakantieregio</StackedList.Term>
                  <StackedList.DescriptionText>
                    <Select<Vakantieregio | typeof GEEN_REGIO>
                      value={school.vakantieregio ?? GEEN_REGIO}
                      onValueChange={(next) =>
                        void handleVakantieregioChange(!next || next === GEEN_REGIO ? null : next)
                      }
                    >
                      <SelectTrigger aria-label="Vakantieregio" className="w-48">
                        <SelectValue>{school.vakantieregio ?? "Onbekend"}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value={GEEN_REGIO}>Onbekend</SelectItem>
                          {VAKANTIEREGIOS.map((regio) => (
                            <SelectItem key={regio} value={regio}>
                              {regio}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </StackedList.DescriptionText>
                </StackedList.Item>
                <StackedList.Item>
                  <StackedList.Term>Zuurpruim</StackedList.Term>
                  <StackedList.DescriptionText>
                    <Switch
                      checked={school.isZuurpruim}
                      onCheckedChange={(checked) => void handleIsZuurpruimChange(checked)}
                    />
                  </StackedList.DescriptionText>
                </StackedList.Item>
                <StackedList.Item>
                  <StackedList.Term>Uitsluiten van statistieken</StackedList.Term>
                  <StackedList.DescriptionText>
                    <Switch
                      checked={school.excludeFromStatistics}
                      onCheckedChange={(checked) => void handleExcludeFromStatisticsChange(checked)}
                    />
                  </StackedList.DescriptionText>
                </StackedList.Item>
                <StackedList.Item>
                  <StackedList.Term>Max aant. leraren</StackedList.Term>
                  <StackedList.DescriptionText>
                    <NumberField
                      aria-label="Max aant. leraren"
                      value={maxDocenten}
                      onValueChange={handleMaxDocentenChange}
                      min={0}
                      className="w-24"
                      withStepper={true}
                    />
                  </StackedList.DescriptionText>
                </StackedList.Item>
              </StackedList.Content>
            </StackedList>

            <StackedList>
              <StackedList.Content>
                {school.licentie ? (
                  <>
                    <StackedList.Item>
                      <StackedList.Term>{m["common.type"]()}</StackedList.Term>
                      <StackedList.DescriptionText>
                        <Select
                          value={licentieType ?? (school.licentie.licentieType || undefined)}
                          onValueChange={(key) => {
                            if (key === "Proefperiode" || key === "Abonnement") {
                              void handleLicentieTypeChange(key);
                            }
                          }}
                        >
                          <SelectTrigger aria-label="Type" className="w-auto">
                            <SelectValue>
                              {getLicentieTypeTranslation(
                                licentieType ?? school.licentie.licentieType,
                              )}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectItem value="Proefperiode">
                                {getLicentieTypeTranslation("Proefperiode")}
                              </SelectItem>
                              <SelectItem value="Abonnement">
                                {getLicentieTypeTranslation("Abonnement")}
                              </SelectItem>
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </StackedList.DescriptionText>
                    </StackedList.Item>
                    <StackedList.Item>
                      <StackedList.Term>{m["common.fields.date"]()}</StackedList.Term>
                      <StackedList.DescriptionText>
                        {school.licentie.startDate
                          ? new Date(school.licentie.startDate).toLocaleDateString("nl-NL")
                          : "-"}
                      </StackedList.DescriptionText>
                    </StackedList.Item>
                    <StackedList.Item>
                      <StackedList.Term>{m["admin.scholen.table.einddatum"]()}</StackedList.Term>
                      <StackedList.DescriptionText>
                        <DatePicker
                          aria-label="Einddatum"
                          hideTimeZone
                          value={endDate}
                          onChange={(date) => void handleEndDateChange(date)}
                          className="w-auto"
                        />
                      </StackedList.DescriptionText>
                    </StackedList.Item>
                    <StackedList.Item>
                      <StackedList.Term>
                        {m["admin.school.aantal_leerlingen_licentie"]()}
                      </StackedList.Term>
                      <StackedList.DescriptionText>
                        <NumberField
                          aria-label={m["admin.school.aantal_leerlingen_licentie"]()}
                          value={aantalLeerlingen}
                          onValueChange={handleAantalLeerlingenChange}
                          min={0}
                          className="w-24"
                          withStepper={true}
                        />
                      </StackedList.DescriptionText>
                    </StackedList.Item>
                    <StackedList.Item>
                      <StackedList.Term>
                        {m["admin.school.daadwerkelijk_aantal_leerlingen"]()}
                      </StackedList.Term>
                      <StackedList.DescriptionText>
                        {school.licentie.actualAantalLeerlingen}
                      </StackedList.DescriptionText>
                    </StackedList.Item>
                    <StackedList.Item>
                      <StackedList.Term>
                        {m["admin.scholen.table.verwerkersovereenkomst"]()}
                      </StackedList.Term>
                      <StackedList.DescriptionText>
                        <Switch
                          checked={school.licentie.verwerkersovereenkomst}
                          onCheckedChange={(checked) =>
                            void handleVerwerkersovereenkomstChange(checked)
                          }
                        />
                      </StackedList.DescriptionText>
                    </StackedList.Item>
                    <StackedList.Item>
                      <StackedList.Term>{m["common.labels.hoofddocent"]()}</StackedList.Term>
                      <StackedList.DescriptionText>
                        {school.hoofdDocent}
                      </StackedList.DescriptionText>
                    </StackedList.Item>
                  </>
                ) : (
                  <StackedList.Item>
                    <StackedList.Term>{m["common.labels.hoofddocent"]()}</StackedList.Term>
                    <StackedList.DescriptionText>{school.hoofdDocent}</StackedList.DescriptionText>
                  </StackedList.Item>
                )}
              </StackedList.Content>
            </StackedList>
          </div>
        </Page.Body>
      </Page.Container>
    </Page>
  );
}
